import { Injectable } from '@angular/core';
import { FormControl,FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import {
AccountsReceivableLedgerTemplateEntity
} from '../../../dave-data-module/entities/accounts-receivable-ledger-template.entity';
import { AccountsReceivableLedgerTypeEnum } from "../../../dave-data-module/entities/accounts-receivable-ledger.entity";
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { MaterialDataService } from '../../../dave-data-module/services/material-data.service';
import { State } from '../../../dave-data-module/State';
import {
getARLTemplateTypes,
getARLTemplateTypesFetched
} from '../../../dave-data-module/State/selectors/accounting.selector';
import { isNotNullOrUndefined,uniqArray } from '../../../helper/helper';
import * as mjsHelper from '../../../helper/math-js-helper';
import { getCostFromArlTemplate } from './arl-calculation.helper';
import { FormControlCache } from "./formcontrol-cache";

export type arlTemplateTableDataArlForm = FormGroup<{
    // Amount: FormControl<number>;
    Information: FormControl<string>;
    BookingText: FormControl<string>;
    BaseCost: FormControl<number>;
    BaseCostGross: FormControl<number>;
    Quantity: FormControl<number>;
    QuantityTypeId: FormControl<number>;
    childrenVisible: FormControl<boolean>;
    InheritFromChildren: FormControl<boolean>;
    ParentId: FormControl<number | null>;
    ARLIds: FormControl<number[]>;
    ZuschlaegeResourcen: FormControl<number>;
    ZuschlaegeDefault: FormControl<number>;
    Type: FormControl<AccountsReceivableLedgerTypeEnum>;
    ResourceId: FormControl<number>;
    Longtext: FormControl<string>;
    ShowLongtext: FormControl<boolean>;
    TemplateTypeName: FormControl<string>;
    Tax: FormControl<number>;
    CurrencyCode: FormControl<string>;
    ZuschlaegeMaterials: FormControl<number>;
    ZuschlaegeEmployee: FormControl<number>;
    ZuschlaegeExternalService: FormControl<number>;
    ZuschlaegeOther: FormControl<number>;
    QuantityFormula: FormControl<string>;
    QuantityFormulaUsedVariables: FormControl<string[]>;
}>;
@Injectable()
export class ArlTemplateFormDataService extends FormControlCache<arlTemplateTableDataArlForm> {
    constructor(private store: Store<State>, private materialDataService: MaterialDataService) {
        super();
    }
    /**
     *
     * @param id Id from arl to remove
     * @return Ids from all childArls including id
     */
    public removeArlWithChildren(id: number) {
        this.arlFormMap.forEach((form) => {
            if (form.value.ARLIds.includes(id)) {
                form.controls.ARLIds.setValue(form.value.ARLIds.filter((arlId) => arlId !== id));
                form.controls.ARLIds.markAsDirty();
            }
        });
        const childIds = this.getChildIds(id);
        childIds.forEach((arlId) => this.arlFormMap.delete(arlId));
        return childIds;
    }
    /**
     *
     * @param id Id from arl
     * @return Ids from all childArls including id
     */
    public getChildIds = (id: number): number[] => {
        if (!this.arlFormMap.has(id)) {
            return [];
        }
        if (this.arlFormMap.get(id).value.ARLIds?.length) {
            return [
                id,
                ...this.arlFormMap
                    .get(id)
                    .value.ARLIds.map((arlId) => this.getChildIds(arlId))
                    .flat(),
            ];
        } else {
            return [id];
        }
    };
    async getFormByEntity(arl: AccountsReceivableLedgerTemplateEntity): Promise<arlTemplateTableDataArlForm> {
        let form: arlTemplateTableDataArlForm = arl && this.getForm(arl.Id);
        if (!form) {
            let baseCost = arl?.BaseCost;
            let baseCostGross = arl?.BaseCostGross;
            if (arl) {
                const c = await getCostFromArlTemplate(arl, this.store, this.materialDataService);
                if (isNotNullOrUndefined(c)) {
                    baseCost = c;
                    baseCostGross = 0;
                }
            }
            let templateTypeName: string = null;
            if (arl?.ARLTemplateTypeId) {
                const templateTypes = await firstValueFrom(getFetched$(this.store, getARLTemplateTypesFetched, getARLTemplateTypes))
                templateTypeName = templateTypes.find(t => t.Id === arl.ARLTemplateTypeId)?.Name
            }
            form = new FormGroup({
                // Amount: new FormControl<number>(e.Amount, {nonNullable: true}),
                Information: new FormControl<string>(arl.Information, { nonNullable: true }),
                BookingText: new FormControl<string>(arl.BookingText, { nonNullable: true }),
                BaseCost: new FormControl<number>(baseCost, { nonNullable: true }),
                BaseCostGross: new FormControl<number>(baseCostGross, { nonNullable: true }),
                Quantity: new FormControl<number>(arl.Quantity, { nonNullable: true }),
                QuantityFormula: new FormControl<string>(arl.QuantityFormula, { nonNullable: true }),
                QuantityTypeId: new FormControl<number>(arl.QuantityTypeId, { nonNullable: true }),
                // wird von der arl-calculation-component verwaltet
                childrenVisible: new FormControl<boolean>(null, { nonNullable: true }),
                InheritFromChildren: new FormControl<boolean>(arl.InheritFromChildren, { nonNullable: true }),
                ParentId: new FormControl<number | null>(arl.ParentId, { nonNullable: true }),
                ARLIds: new FormControl<number[]>(uniqArray(arl.ArlTemplateIds) || [], { nonNullable: true }),
                ZuschlaegeResourcen: new FormControl<number>(arl.CustomProperties?.ZuschlaegeResourcen || null, { nonNullable: true }),
                ZuschlaegeDefault: new FormControl<number>(arl.CustomProperties?.ZuschlaegeDefault || null, { nonNullable: true }),
                Type: new FormControl<AccountsReceivableLedgerTypeEnum>(arl.Type || null, { nonNullable: true }),
                ResourceId: new FormControl<number>(arl.ResourceId || null, { nonNullable: true }),
                Longtext: new FormControl<string>(arl.Longtext || null, { nonNullable: true }),
                ShowLongtext: new FormControl<boolean>(arl.ShowLongtext || false, { nonNullable: true }),
                TemplateTypeName: new FormControl<string>(templateTypeName || null, { nonNullable: true }),
                Tax: new FormControl<number>(arl.Tax || null, { nonNullable: true }),
                CurrencyCode: new FormControl<string>(arl.CurrencyCode || null, { nonNullable: true }),
                ZuschlaegeMaterials: new FormControl<number>(arl.CustomProperties?.ZuschlaegeMaterials || null, { nonNullable: true }),
                ZuschlaegeEmployee: new FormControl<number>(arl.CustomProperties?.ZuschlaegeEmployee || null, { nonNullable: true }),
                ZuschlaegeExternalService: new FormControl<number>(arl.CustomProperties?.ZuschlaegeExternalService || null, { nonNullable: true }),
                ZuschlaegeOther: new FormControl<number>(arl.CustomProperties?.ZuschlaegeOther || null, { nonNullable: true }),
                QuantityFormulaUsedVariables: new FormControl<string[]>(arl.QuantityFormula ? mjsHelper.getUsedVariableNames(arl.QuantityFormula) : [], { nonNullable: true }),
            });
            this.setForm(arl.Id, form);
        }
        return form;
    }
}
